import { antGrey, appHomeCardsWidgetTitleStyle, runwayColors } from '@/components/styles/globalStyles';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { AppStoreAvailableBuild, IntegrationType, SubscriptionFeatureType, DestinationPhasedRelease } from '@/types';
import Space from 'antd/lib/space';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Progress from 'antd/lib/progress';
import Tooltip from 'antd/lib/tooltip';
import { getIntegrationTypeForAppStoreType } from '@/helpers';
import { displayTextForPausedOrAcceleratedPhasedReleaseGroup } from '@/helpers/displayHelpers';
import { BlockOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { Destination } from '@/types/destinations';
import { PhasedReleaseChartBase, PhasedReleaseDaysSummary } from '@/components/appHome/widgets/LiveReleaseChartWidget';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';
import { PlanRestrictedFeatureWrapper } from '@/components/planRestrictions/PlanRestrictedFeatureWrapper';
import Button from 'antd/lib/button';

export interface WorkflowRunPhasedReleaseWidgetsContainerProps {
    phasedGroups: DestinationPhasedRelease[][];
    appStoreBuildsByDestStepConfigId: Record<string, AppStoreAvailableBuild[]>;
    stabIntegrationId?: IntegrationType;
    allDestinations: Destination[];
    selectedDestinationStepConfigIds: string[];
    userSelectedMultipleAPKBuildId?: string;
}

export const WorkflowRunPhasedReleaseWidgetsContainer: React.FC<WorkflowRunPhasedReleaseWidgetsContainerProps> = ({
    phasedGroups,
    appStoreBuildsByDestStepConfigId,
    stabIntegrationId,
    userSelectedMultipleAPKBuildId,
}) => {
    const [expandedModule, setExpandedModule] = React.useState<Record<number, boolean>>({});

    if (!phasedGroups || phasedGroups.length === 0) {
        return null;
    }

    React.useEffect(() => {
        setExpandedModule({});
    }, []);

    return (
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
            {(isDisabled) => (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 10,
                    }}
                >
                    {phasedGroups.slice(0, isDisabled ? 1 : undefined).map((groupedPhasedReleases, i) => {
                        return (
                            <PhasedReleaseGroupWidget
                                key={i}
                                groupKey={i}
                                phasedGroupData={groupedPhasedReleases}
                                appStoreBuildsByDestStepConfigId={appStoreBuildsByDestStepConfigId}
                                userSelectedMultipleAPKBuildId={userSelectedMultipleAPKBuildId ?? null}
                                stabIntegrationId={stabIntegrationId} // TODO-WF-OVERVIEW: tweak component logic to support multiple stab integrations (not yet supported)
                                isExpandedModule={expandedModule[i] === true}
                                setExpandedModule={setExpandedModule}
                                onlyShowAdoptionsOnExpand={phasedGroups.length > 2}
                            />
                        );
                    })}
                </div>
            )}
        </PlanRestrictedFeatureWrapper>
    );
};

export interface PhasedReleaseGroupWidgetProps {
    groupKey: number;
    phasedGroupData: DestinationPhasedRelease[];
    appStoreBuildsByDestStepConfigId: Record<string, AppStoreAvailableBuild[]>;
    userSelectedMultipleAPKBuildId?: string;
    stabIntegrationId?: IntegrationType;
    isExpandedModule: boolean;
    setExpandedModule: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
    onlyShowAdoptionsOnExpand: boolean;
    shouldHideAdoptions?: boolean;
}

export const PhasedReleaseGroupWidget: React.FC<PhasedReleaseGroupWidgetProps> = ({
    groupKey,
    phasedGroupData,
    appStoreBuildsByDestStepConfigId,
    userSelectedMultipleAPKBuildId,
    stabIntegrationId,
    isExpandedModule,
    setExpandedModule,
    onlyShowAdoptionsOnExpand,
    shouldHideAdoptions,
}) => {
    const groupDestinations = phasedGroupData.map((destinationPhasedRelease) => destinationPhasedRelease.destination);

    const groupStoreIntegrationIds = [
        ...new Set(
            phasedGroupData.map((destPhasedInfo) =>
                getIntegrationTypeForAppStoreType(destPhasedInfo.destination.appStoreType),
            ),
        ),
    ];
    const iconsAndAltForAllStoreIntegrationTypes: [string, string][] = groupStoreIntegrationIds.map((integrationId) => {
        return iconAndAltForIntegrationType(integrationId);
    });

    const haveStabIntegrationId = !!stabIntegrationId; // TODO-WF-OVERVIEW: tweak component logic to support multiple stab integrations (not yet supported)
    const [stabIcon, stabAlt] = iconAndAltForIntegrationType(stabIntegrationId);

    const groupDestinationAdoptions: [Destination, number][] = React.useMemo(() => {
        const groupAdoptionsResult: [Destination, number][] = [];
        for (const destination of groupDestinations) {
            const destStepConfigId = destination.stepConfigId;

            const availableDestBuilds = appStoreBuildsByDestStepConfigId[destStepConfigId];
            const haveDestBuilds = availableDestBuilds?.length > 0;

            if (!haveDestBuilds) {
                continue;
            }

            let destAdoptionRate: number;
            if (userSelectedMultipleAPKBuildId) {
                for (const availableBuild of availableDestBuilds) {
                    if (
                        availableBuild.stabilityInfo?.buildIdentifier &&
                        availableBuild.stabilityInfo?.buildIdentifier === userSelectedMultipleAPKBuildId
                    ) {
                        destAdoptionRate = availableBuild.stabilityInfo.sessionAdoption;
                    }
                }
            } else {
                destAdoptionRate = availableDestBuilds[0].stabilityInfo?.sessionAdoption;
            }

            const haveDestAdoption = !!destAdoptionRate;
            if (haveDestAdoption) {
                groupAdoptionsResult.push([destination, destAdoptionRate]);
            }
        }

        groupAdoptionsResult.sort((adoptionDatumA, adoptionDatumB) => {
            const adoptionA = adoptionDatumA[1];
            const adoptionB = adoptionDatumB[1];

            if (adoptionA > adoptionB) {
                return -1;
            } else if (adoptionA < adoptionB) {
                return 1;
            } else {
                return 0;
            }
        });

        return groupAdoptionsResult;
    }, [phasedGroupData, appStoreBuildsByDestStepConfigId, userSelectedMultipleAPKBuildId, stabIntegrationId]);

    const haveGroupAdoptions = groupDestinationAdoptions.length > 0;

    const firstGroupPhasedDatum = phasedGroupData[0];

    const groupPercent = firstGroupPhasedDatum.phasedReleasePercent;
    const groupDay = firstGroupPhasedDatum.dayNumber;

    const groupPhasedConfig = firstGroupPhasedDatum.daysToPercentSchedule;
    const groupRolloutsAutomated = firstGroupPhasedDatum.isAutomatedPhasedRollout;

    const groupIsAccelerated = firstGroupPhasedDatum.isAccelerated;
    const groupWasRWYAccelerated = firstGroupPhasedDatum.isRunwayAcceleratedRollout;

    const groupIsHalted = firstGroupPhasedDatum.isPaused;
    const groupWasRWYHalted = firstGroupPhasedDatum.isRunwayPaused;

    return (
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
            {(isDisabled) => (
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: -1,
                            left: -1,
                            right: 1,
                            gap: 20,
                            width: 'calc(100% + 2px)',
                            zIndex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            borderRadius: 4,
                            backgroundColor: antGrey.grey1,
                            border: shouldHideAdoptions ? 'none' : `1px solid ${antGrey.grey5}`,
                            padding: '16px 20px',
                            boxSizing: 'border-box',
                            marginBottom: 8,
                            boxShadow:
                                isExpandedModule && !shouldHideAdoptions ? '0px 4px 8px rgba(0, 0, 0, 0.2)' : null,
                            transition: 'all 0.2s ease-in-out',
                            ...(isExpandedModule ?
                                {
                                    opacity: 1,
                                }
                            :   {
                                    opacity: 0,
                                    pointerEvents: 'none',
                                }),
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                gap: 8,
                                alignItems: 'stretch',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Space
                                    split={
                                        <div
                                            style={{
                                                width: 1,
                                                height: 10,
                                                backgroundColor: antGrey.grey5,
                                            }}
                                        />
                                    }
                                    size={8}
                                >
                                    <Space>
                                        {iconsAndAltForAllStoreIntegrationTypes.length === 1 && (
                                            <img
                                                src={iconsAndAltForAllStoreIntegrationTypes[0][0]}
                                                alt={iconsAndAltForAllStoreIntegrationTypes[0][1]}
                                                width={16}
                                                height={16}
                                            />
                                        )}
                                        {iconsAndAltForAllStoreIntegrationTypes.length > 1 && (
                                            <Tooltip
                                                title={
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {iconsAndAltForAllStoreIntegrationTypes.map(
                                                            ([icon, alt], i) => {
                                                                return (
                                                                    <img
                                                                        key={i}
                                                                        src={icon}
                                                                        alt={alt}
                                                                        width={16}
                                                                        height={16}
                                                                    />
                                                                );
                                                            },
                                                        )}
                                                    </div>
                                                }
                                                color={runwayColors.white}
                                            >
                                                <BlockOutlined style={{ color: antGrey.grey6 }} size={12} />
                                            </Tooltip>
                                        )}
                                        <Text
                                            style={{
                                                ...appHomeCardsWidgetTitleStyle,
                                            }}
                                        >
                                            Phased Release
                                        </Text>
                                        <PlanRestrictedFeatureWrapper
                                            feature={SubscriptionFeatureType.Rollouts}
                                            requiresTooltip
                                            mini
                                        />
                                    </Space>
                                    {(groupIsAccelerated || groupIsHalted) && (
                                        <Text
                                            style={{
                                                ...appHomeCardsWidgetTitleStyle,
                                                color:
                                                    groupIsAccelerated ?
                                                        runwayColors.statusGreen
                                                    :   runwayColors.statusRed,
                                            }}
                                        >
                                            {displayTextForPausedOrAcceleratedPhasedReleaseGroup(
                                                groupIsHalted,
                                                groupWasRWYHalted,
                                                groupIsAccelerated,
                                                groupWasRWYAccelerated,
                                            )}
                                        </Text>
                                    )}
                                </Space>
                                {!isDisabled && (
                                    <PhasedReleaseDaysSummary
                                        percentage={groupPercent}
                                        dayNumber={groupDay}
                                        daysToPercent={groupPhasedConfig}
                                        isAutomatedPhasedRollout={groupRolloutsAutomated}
                                        isRunwayAcceleratedRollout={groupIsAccelerated && groupWasRWYAccelerated}
                                        isRunwayRolloutPaused={groupIsHalted && groupWasRWYHalted}
                                    />
                                )}
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 10,
                                }}
                            >
                                {!isDisabled ?
                                    <>
                                        <div
                                            style={{
                                                flexBasis: '18px',
                                                flexShrink: 0,
                                                flexGrow: 0,
                                            }}
                                        >
                                            <DestinationCounter destinations={groupDestinations} size={18} />
                                        </div>
                                        <PhasedReleaseChartBase
                                            percentage={groupPercent}
                                            dayNumber={groupDay}
                                            daysToPercent={groupPhasedConfig}
                                            isAutomatedPhasedRollout={groupRolloutsAutomated}
                                            wasHalted={groupIsHalted}
                                            wasAccelerated={groupIsAccelerated}
                                        />
                                    </>
                                :   <Progress
                                        className="widget-progress-bar"
                                        strokeLinecap="round"
                                        showInfo={false}
                                        strokeColor={runwayColors.primary}
                                        percent={0}
                                        trailColor={antGrey.grey4}
                                        style={{
                                            flexGrow: 1,
                                            margin: 0,
                                        }}
                                        size={['100%', 8]}
                                    />
                                }
                            </div>
                        </div>
                        {!shouldHideAdoptions && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                    gap: 8,
                                    alignItems: 'stretch',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Space>
                                        {haveStabIntegrationId && stabIcon && (
                                            <img src={stabIcon} alt={stabAlt} width={16} height={16} />
                                        )}
                                        <Text
                                            style={{
                                                ...appHomeCardsWidgetTitleStyle,
                                            }}
                                        >
                                            Adoption rate (sessions)
                                        </Text>
                                        <PlanRestrictedFeatureWrapper
                                            feature={SubscriptionFeatureType.Rollouts}
                                            requiresTooltip
                                            mini
                                        />
                                    </Space>

                                    {(groupDestinationAdoptions.length > 5 || onlyShowAdoptionsOnExpand) && (
                                        <Button
                                            onClick={() =>
                                                isExpandedModule ?
                                                    setExpandedModule({})
                                                :   setExpandedModule({ [groupKey]: true })
                                            }
                                            style={{
                                                height: 16,
                                                width: 16,
                                                padding: 0,
                                                margin: 0,
                                                borderWidth: 0,
                                            }}
                                            size={'middle'}
                                            icon={
                                                isExpandedModule === true ?
                                                    <ZoomOutOutlined
                                                        style={{
                                                            color: antGrey.grey6,
                                                            verticalAlign: 'baseline',
                                                            margin: 0,
                                                        }}
                                                        size={16}
                                                    />
                                                :   <ZoomInOutlined
                                                        style={{
                                                            color: antGrey.grey6,
                                                            verticalAlign: 'baseline',
                                                            margin: 0,
                                                        }}
                                                        size={16}
                                                    />
                                            }
                                            type="link"
                                        />
                                    )}
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'stretch',
                                        gap: 6,
                                    }}
                                >
                                    {haveGroupAdoptions &&
                                        groupDestinationAdoptions.map(([destination, adoption], i) => {
                                            return (
                                                <DestinationAdoptionWidgetRow
                                                    key={`i=${i}&percent=${adoption}`}
                                                    destination={destination}
                                                    adoptionPercentage={adoption}
                                                    obfuscateRowData={isDisabled}
                                                />
                                            );
                                        })}
                                    {haveGroupAdoptions === false && (
                                        <Text
                                            style={{
                                                color: antGrey.grey6,
                                                fontWeight: 300,
                                                fontSize: 12,
                                            }}
                                        >
                                            No adoption rates known.
                                        </Text>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            gap: 20,
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            borderRadius: 4,
                            backgroundColor: antGrey.grey1,
                            border: `1px solid ${antGrey.grey5}`,
                            padding: '16px 20px',
                            boxSizing: 'border-box',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                gap: 8,
                                alignItems: 'stretch',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Space
                                    split={
                                        <div
                                            style={{
                                                width: 1,
                                                height: 10,
                                                backgroundColor: antGrey.grey5,
                                            }}
                                        />
                                    }
                                    size={8}
                                >
                                    <Space>
                                        {iconsAndAltForAllStoreIntegrationTypes.length === 1 && (
                                            <img
                                                src={iconsAndAltForAllStoreIntegrationTypes[0][0]}
                                                alt={iconsAndAltForAllStoreIntegrationTypes[0][1]}
                                                width={16}
                                                height={16}
                                            />
                                        )}
                                        {iconsAndAltForAllStoreIntegrationTypes.length > 1 && (
                                            <Tooltip
                                                title={
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {iconsAndAltForAllStoreIntegrationTypes.map(
                                                            ([icon, alt], i) => {
                                                                return (
                                                                    <img
                                                                        key={i}
                                                                        src={icon}
                                                                        alt={alt}
                                                                        width={16}
                                                                        height={16}
                                                                    />
                                                                );
                                                            },
                                                        )}
                                                    </div>
                                                }
                                                color={runwayColors.white}
                                            >
                                                <BlockOutlined style={{ color: antGrey.grey6 }} size={12} />
                                            </Tooltip>
                                        )}
                                        <Text
                                            style={{
                                                ...appHomeCardsWidgetTitleStyle,
                                            }}
                                        >
                                            Phased Release
                                        </Text>
                                        <PlanRestrictedFeatureWrapper
                                            feature={SubscriptionFeatureType.Rollouts}
                                            requiresTooltip
                                            mini
                                        />
                                    </Space>
                                    {(groupIsAccelerated || groupIsHalted) && (
                                        <Text
                                            style={{
                                                ...appHomeCardsWidgetTitleStyle,
                                                color:
                                                    groupIsAccelerated ?
                                                        runwayColors.statusGreen
                                                    :   runwayColors.statusRed,
                                            }}
                                        >
                                            {displayTextForPausedOrAcceleratedPhasedReleaseGroup(
                                                groupIsHalted,
                                                groupWasRWYHalted,
                                                groupIsAccelerated,
                                                groupWasRWYAccelerated,
                                            )}
                                        </Text>
                                    )}
                                </Space>
                                {!isDisabled && (
                                    <PhasedReleaseDaysSummary
                                        percentage={groupPercent}
                                        dayNumber={groupDay}
                                        daysToPercent={groupPhasedConfig}
                                        isAutomatedPhasedRollout={groupRolloutsAutomated}
                                        isRunwayAcceleratedRollout={groupIsAccelerated && groupWasRWYAccelerated}
                                        isRunwayRolloutPaused={groupIsHalted && groupWasRWYHalted}
                                    />
                                )}
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 10,
                                }}
                            >
                                {!isDisabled ?
                                    <>
                                        <div
                                            style={{
                                                flexBasis: '18px',
                                                flexShrink: 0,
                                                flexGrow: 0,
                                            }}
                                        >
                                            <DestinationCounter destinations={groupDestinations} size={18} />
                                        </div>
                                        <PhasedReleaseChartBase
                                            percentage={groupPercent}
                                            dayNumber={groupDay}
                                            daysToPercent={groupPhasedConfig}
                                            isAutomatedPhasedRollout={groupRolloutsAutomated}
                                            wasHalted={groupIsHalted}
                                            wasAccelerated={groupIsAccelerated}
                                        />
                                    </>
                                :   <Progress
                                        className="widget-progress-bar"
                                        strokeLinecap="round"
                                        showInfo={false}
                                        strokeColor={runwayColors.primary}
                                        percent={0}
                                        trailColor={antGrey.grey4}
                                        style={{
                                            flexGrow: 1,
                                            margin: 0,
                                        }}
                                        size={['100%', 8]}
                                    />
                                }
                            </div>
                        </div>
                        {!shouldHideAdoptions && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                    gap: 8,
                                    alignItems: 'stretch',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        justifyItems: 'end',
                                    }}
                                >
                                    <Space>
                                        {!onlyShowAdoptionsOnExpand && (
                                            <>
                                                {haveStabIntegrationId && stabIcon && (
                                                    <img src={stabIcon} alt={stabAlt} width={16} height={16} />
                                                )}
                                                <Text
                                                    style={{
                                                        ...appHomeCardsWidgetTitleStyle,
                                                    }}
                                                >
                                                    Adoption rate (sessions)
                                                </Text>
                                                <PlanRestrictedFeatureWrapper
                                                    feature={SubscriptionFeatureType.Rollouts}
                                                    requiresTooltip
                                                    mini
                                                />
                                            </>
                                        )}
                                    </Space>
                                    {(groupDestinationAdoptions.length > 5 || onlyShowAdoptionsOnExpand) && (
                                        <Button
                                            onClick={() =>
                                                isExpandedModule ?
                                                    setExpandedModule({})
                                                :   setExpandedModule({ [groupKey]: true })
                                            }
                                            style={{
                                                height: 16,
                                                width: 16,
                                                padding: 0,
                                                margin: 0,
                                                borderWidth: 0,
                                            }}
                                            size={'middle'}
                                            icon={
                                                isExpandedModule === true ?
                                                    <ZoomOutOutlined
                                                        style={{
                                                            color: antGrey.grey6,
                                                            verticalAlign: 'baseline',
                                                            margin: 0,
                                                        }}
                                                        size={16}
                                                    />
                                                :   <ZoomInOutlined
                                                        style={{
                                                            color: antGrey.grey6,
                                                            verticalAlign: 'baseline',
                                                            margin: 0,
                                                        }}
                                                        size={16}
                                                    />
                                            }
                                            type="link"
                                        />
                                    )}
                                </div>
                                {!onlyShowAdoptionsOnExpand && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'stretch',
                                            gap: 6,
                                        }}
                                    >
                                        {haveGroupAdoptions &&
                                            groupDestinationAdoptions.slice(0, 5).map(([destination, adoption], i) => {
                                                return (
                                                    <DestinationAdoptionWidgetRow
                                                        key={`i=${i}&percent=${adoption}`}
                                                        destination={destination}
                                                        adoptionPercentage={adoption}
                                                        obfuscateRowData={isDisabled}
                                                    />
                                                );
                                            })}
                                        {!haveGroupAdoptions && (
                                            <Text
                                                style={{
                                                    color: antGrey.grey6,
                                                    fontWeight: 300,
                                                    fontSize: 12,
                                                }}
                                            >
                                                No adoption rates known.
                                            </Text>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </PlanRestrictedFeatureWrapper>
    );
};

interface DestinationAdoptionWidgetRowProps {
    destination: Destination;
    adoptionPercentage: number;
    obfuscateRowData: boolean;
}

export const DestinationAdoptionWidgetRow: React.FC<DestinationAdoptionWidgetRowProps> = ({
    destination,
    adoptionPercentage,
    obfuscateRowData,
}) => {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'nowrap',
                fontSize: 10,
                height: 22,
                lineHeight: '22px',
                flexShrink: 0,
                flexGrow: 0,
            }}
        >
            <div
                style={{
                    flexShrink: 0,
                    flexGrow: 0,
                    flexBasis: '18px',
                }}
            >
                <DestinationCounter destinations={[destination]} size={18} />
            </div>
            <Progress
                className="widget-progress-bar"
                strokeLinecap="round"
                percent={obfuscateRowData ? 0 : adoptionPercentage}
                showInfo={false}
                strokeColor={runwayColors.primary}
                trailColor={antGrey.grey4}
                style={{
                    margin: 0,
                }}
                size={['100%', 8]}
            />
            {!obfuscateRowData && (
                <span
                    style={{
                        flexShrink: 0,
                        color: antGrey.grey7,
                        fontWeight: 300,
                        fontSize: 10,
                    }}
                >
                    {`${adoptionPercentage.toFixed(1)}%`}
                </span>
            )}
        </div>
    );
};
